import TenantPrivacyHistoryFieldNames from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyHistoryFieldNames"
import TenantPrivacyHistoryTransition from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyHistoryTransition"
import { PRIVACY_CHANGE_KIND_REMOVED } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"
import {
  describeFieldGroup,
  groupFieldChanges,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"
import type {
  PrivacyField,
  PrivacySourceChange,
  PrivacyVisibilityType,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyHistorySourceChangeProps = {
  change: PrivacySourceChange
  fields: PrivacyField[]
  visibilityTypes: PrivacyVisibilityType[]
}

// A write that moved no code still happened; say what it was instead of
// leaving the row bare. A removal of an all-hidden row and a re-applied
// preset are the two ways that occurs.
function unchangedNote(change: PrivacySourceChange) {
  if (change.Kind === PRIVACY_CHANGE_KIND_REMOVED) {
    return "Already hidden on every field"
  }
  return "No field changed"
}

// One source inside one write, as rows of the section's two-column grid:
// the source's name across both columns, then one row per distinct
// before → after — which fields on the left, the transition on the right.
// Rendering rows rather than a box is what lines every transition in the
// section up in one column, so the eye scans a single axis.
function TenantPrivacyHistorySourceChange(
  props: TenantPrivacyHistorySourceChangeProps,
) {
  const { change, fields, visibilityTypes } = props

  const groups = groupFieldChanges(change.Fields)

  return (
    <>
      <div className="col-span-2 truncate pt-2 text-sm font-medium first:pt-0">
        {change.ExternalSourceName ?? `Source ${change.ExternalSourceCode}`}
      </div>

      {groups.length === 0 ? (
        <div className="col-span-2 pl-3 text-muted-foreground">
          {unchangedNote(change)}
        </div>
      ) : (
        groups.map((group) => (
          <div key={`${group.fromCode}-${group.toCode}`} className="contents">
            <div className="min-w-0 pl-3">
              <TenantPrivacyHistoryFieldNames
                description={describeFieldGroup(group.columnNames, fields)}
              />
            </div>
            <div className="justify-self-end">
              <TenantPrivacyHistoryTransition
                fromCode={group.fromCode}
                toCode={group.toCode}
                visibilityTypes={visibilityTypes}
              />
            </div>
          </div>
        ))
      )}
    </>
  )
}

export default TenantPrivacyHistorySourceChange
